import { DndContext, DragOverlay } from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  Button,
  Div,
  ErrorToast,
  H3,
  Icon,
  Img,
  P,
  PlusIcon,
  SuccessToast,
  createMenuCategory,
  deleteMenuTypeItemById,
  noCategories,
  updateMenuCategory,
  updateMenuCategoryOrder,
  validateMenuItemDescription,
  validateMenuItemName,
  reportError,
} from '@vgrubs/components';
import { CreateMenuCategoryV2Dto } from 'components/src/types/api/menu.types';
import {
  ItemType,
  MenuAvailabilityV2Response,
  CategoryV2Response,
  MenuV2Response,
  UpdateMenuListOrders,
} from 'components/src/types/menu.types';
import { Dispatch, SetStateAction, useState } from 'react';
import { useParams } from 'react-router';
import { useSelectedMenu } from '../../menuSelection';
import { AddAvailabilityModal } from './AddAvailabilityModal';
import { AddCategoryModal } from './AddCategoryModal';
import { CategoryComponent } from './CategoryComponent';
import { DeleteCategoryModal } from './DeleteCategoryModal';

export const Categories = ({
  menu,
  setMenu,
  setLoading,
  handleChangeShouldPublish,
  isPos,
}: {
  menu: MenuV2Response | null;
  setMenu: Dispatch<SetStateAction<MenuV2Response | null>>;
  setLoading: Dispatch<SetStateAction<boolean>>;
  handleChangeShouldPublish: (shouldPublish: boolean) => void;
  isPos: boolean;
}) => {
  const { menuId } = useSelectedMenu();
  const [addModal, setAddModal] = useState(false);
  const [editModal, setEditModal] = useState<CategoryV2Response | null>(null);
  const [addAvailabilityModal, setAddAvailabilityModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState<number | null>(null);
  const [availability, setAvailability] = useState<
    MenuAvailabilityV2Response[]
  >([]);
  const [activeId, setActiveId] = useState<number | null>(null);

  const [isSorting, setIsSorting] = useState<MenuV2Response | null>(null);
  const { locationId, brandId } = useParams() as {
    locationId: string;
    brandId: string;
  };
  async function handleEditCategory(
    name: CreateMenuCategoryV2Dto['name'],
    description: CreateMenuCategoryV2Dto['description'],
    scheduleIds: CreateMenuCategoryV2Dto['scheduleIds'],
    emptyForm: () => void
  ) {
    const body: CreateMenuCategoryV2Dto = {
      name,
      description,
      scheduleIds,
    };

    if (!name) return ErrorToast('Please enter category name');
    if (!validateMenuItemName(name)) return;
    if (!validateMenuItemDescription(description)) return;

    if (!editModal) return;
    try {
      setLoading(true);

      const { data } = await updateMenuCategory(
        locationId,
        brandId,
        editModal.id,
        body
      );
      emptyForm();
      SuccessToast(data.message);
      setMenu(data.data);
      setEditModal(null);
      handleChangeShouldPublish(true);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  async function handleAddCategory(
    name: CreateMenuCategoryV2Dto['name'],
    description: CreateMenuCategoryV2Dto['description'],
    scheduleIds: CreateMenuCategoryV2Dto['scheduleIds'],
    emptyForm: () => void
  ) {
    const body: CreateMenuCategoryV2Dto = {
      name,
      description,
      scheduleIds,
    };

    if (!name) return ErrorToast('Please enter category name');
    if (!validateMenuItemName(name)) return;
    if (!validateMenuItemDescription(description)) return;
    try {
      setLoading(true);
      const { data } = await createMenuCategory(
        locationId,
        brandId,
        body,
        menuId
      );
      emptyForm();
      setMenu(data.data);
      SuccessToast(data.message);
      setAddModal(false);
      handleChangeShouldPublish(true);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  async function handleDeleteCategory() {
    if (!deleteModal) return;
    try {
      setLoading(true);

      const { data } = await deleteMenuTypeItemById(
        locationId,
        brandId,
        deleteModal,
        ItemType.Category
      );
      setMenu(data.data);
      SuccessToast(data.message);
      setLoading(false);
      setDeleteModal(null);
      handleChangeShouldPublish(true);
    } catch (error) {
      reportError(error);
    }
  }

  const AddCategoryButton = () => {
    return (
      <Button $selected onClick={() => setAddModal(true)}>
        Add Category
      </Button>
    );
  };

  function dragEndEvent(e: any) {
    const { over, active } = e;
    moveItem({ over, active });
  }

  const moveItem = ({
    over,
    active,
  }: {
    over: { id: number };
    active: { id: number };
  }) => {
    const items = menu?.categories;
    if (!items) return;

    const sortedCategories = arrayMove(
      items,
      items.findIndex((category) => category.id === active.id),
      items.findIndex((category) => category.id === over?.id)
    );

    setMenu((prev) => {
      if (!prev) return prev;
      return { ...prev, categories: sortedCategories };
    });
  };

  function handleDragStart(event: any) {
    setActiveId(event.active.id);
  }

  function handleDragEnd(event: any) {
    if (event.over) {
      setActiveId(null);
      dragEndEvent(event);
    }
  }

  async function handleSaveOrder() {
    try {
      setLoading(true);
      if (!menu?.categories) return;
      const list: UpdateMenuListOrders[] = menu?.categories.map(
        (category, index) => ({
          id: category.id,
          order: index + 1,
        })
      );
      const { data } = await updateMenuCategoryOrder(
        locationId,
        brandId,
        list,
        menuId
      );
      setMenu(data.data);
      SuccessToast(data.message);
      setIsSorting(null);
      handleChangeShouldPublish(true);
      setLoading(false);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  const SortingButtons = () => {
    return (
      <>
        {isSorting && (
          <Button
            $selected
            $small
            $gray
            $w="130px"
            $wSM="calc(50% - 8px)"
            onClick={() => {
              setMenu(isSorting);
              setIsSorting(null);
            }}
          >
            Cancel Sorting
          </Button>
        )}
        <Button
          $selected={!!isSorting}
          $small
          $w="130px"
          $wSM={!isSorting ? '100%' : 'calc(50% - 8px)'}
          onClick={() => {
            if (!!isSorting) {
              return handleSaveOrder();
            }
            setIsSorting(menu);
          }}
        >
          {isSorting ? 'Save Sorting' : 'Sort Categories'}
        </Button>
      </>
    );
  };

  return (
    <>
      <Div $flex $between $alignCenter $mb="20">
        <H3 $mb="0">Categories ({menu?.categories.length})</H3>
        <Div $flex $alignCenter $gap="12px">
          <Div $flex $gap="16px" $desktop>
            <SortingButtons />
          </Div>

          {!isPos && menu?.categories && menu?.categories?.length > 0 && (
            <Icon $pointer onClick={() => setAddModal(true)}>
              <PlusIcon />
            </Icon>
          )}
        </Div>
      </Div>

      <Div $flex $gap="16px" $mobile $mb="20">
        <SortingButtons />
      </Div>

      <DndContext onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
        <SortableContext
          items={menu?.categories?.map((task) => task.id) || []}
          strategy={verticalListSortingStrategy}
        >
          {menu?.categories?.map((item) => (
            <CategoryComponent
              key={item.id}
              item={item}
              onClick={(item) => setEditModal(item)}
              onClickDelete={(id) => setDeleteModal(id)}
              isSorting={!!isSorting}
              isPos={isPos}
            />
          ))}
        </SortableContext>
        <DragOverlay>
          {activeId ? (
            <CategoryComponent
              isSorting={!!isSorting}
              isPos={isPos}
              item={
                menu?.categories?.find(
                  (t) => activeId === t.id
                ) as CategoryV2Response
              }
            />
          ) : null}
        </DragOverlay>
      </DndContext>

      {menu?.categories && menu?.categories?.length < 1 && (
        <Div $flex $column $alignCenter>
          <Img alt="" $width="180px" src={noCategories} $mb="24" />
          <H3 $mb="24" $center>
            No Categories
          </H3>
          <P $mb="32" $center>
            This provider currently don’t have <br /> menu categories.
          </P>
          {!isPos && <AddCategoryButton />}
        </Div>
      )}

      <AddCategoryModal
        addModal={addModal}
        closeModal={() => {
          setAddModal(false);
        }}
        availability={availability}
        setAvailability={setAvailability}
        setAddAvailabilityModal={setAddAvailabilityModal}
        handleAddCategory={handleAddCategory}
        // onClickDelete={(id) => setDeleteModal(id)}
        isPos={isPos}
      />

      <AddCategoryModal
        addModal={editModal}
        closeModal={() => {
          setEditModal(null);
        }}
        availability={availability}
        setAvailability={setAvailability}
        setAddAvailabilityModal={setAddAvailabilityModal}
        handleEditCategory={handleEditCategory}
        edit
        isPos={isPos}
      />

      <AddAvailabilityModal
        addAvailabilityModal={addAvailabilityModal}
        closeModal={() => {
          setAddAvailabilityModal(false);
        }}
        availability={availability}
        setAvailability={setAvailability}
        menu={menu}
      />

      <DeleteCategoryModal
        deleteModal={deleteModal}
        closeModal={() => {
          setDeleteModal(null);
        }}
        handleDeleteCategory={handleDeleteCategory}
      />
    </>
  );
};
